import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw } from "lucide-react";
import { LOGO_URL } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem("ooe_admin_key") || "");
  const [authed, setAuthed] = useState(!!localStorage.getItem("ooe_admin_key"));
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchInquiries = async (adminKey) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/inquiries`, { headers: { "X-Admin-Key": adminKey } });
      setInquiries(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Invalid admin key");
        localStorage.removeItem("ooe_admin_key");
        setAuthed(false);
      } else {
        toast.error("Failed to load inquiries");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed && key) fetchInquiries(key);
  }, [authed, key]);

  const login = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/login`, { key });
      localStorage.setItem("ooe_admin_key", key);
      setAuthed(true);
      toast.success("Signed in");
    } catch {
      toast.error("Invalid key");
    }
  };

  const logout = () => {
    localStorage.removeItem("ooe_admin_key");
    setKey("");
    setAuthed(false);
    setInquiries([]);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/inquiries/${id}`, { status }, { headers: { "X-Admin-Key": key } });
      setInquiries((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
    } catch {
      toast.error("Update failed");
    }
  };

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--ooe-bg-2)" }}>
        <form onSubmit={login} className="w-full max-w-md p-10" style={{ background: "var(--ooe-bg)", border: "1px solid var(--ooe-border)" }} data-testid="admin-login-form">
          <img src={LOGO_URL} alt="" className="h-14 w-14 mb-6" />
          <div className="eyebrow mb-3">Admin</div>
          <h1 className="font-serif-display text-3xl mb-8" style={{ color: "var(--ooe-ink)" }}>Out of Eden — Inquiries</h1>
          <label className="label-line">Admin key</label>
          <input
            className="input-line"
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            data-testid="admin-key-input"
            required
          />
          <button type="submit" className="btn-primary mt-8" data-testid="admin-login-btn">Sign in</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--ooe-bg)" }} data-testid="admin-page">
      <header className="border-b" style={{ borderColor: "var(--ooe-border)" }}>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="" className="h-10 w-10" />
            <div>
              <div className="font-serif-display text-xl" style={{ color: "var(--ooe-brand)" }}>Out of Eden</div>
              <div className="eyebrow" style={{ fontSize: 9 }}>Admin · Inquiries</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => fetchInquiries(key)} className="btn-ghost" data-testid="admin-refresh-btn">
              <RefreshCw size={14} /> Refresh
            </button>
            <button onClick={logout} className="btn-ghost" data-testid="admin-logout-btn">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="eyebrow mb-3">Inbox</div>
            <h1 className="font-serif-display text-4xl lg:text-5xl" style={{ color: "var(--ooe-ink)" }}>{inquiries.length} {inquiries.length === 1 ? "inquiry" : "inquiries"}</h1>
          </div>
        </div>

        {loading ? (
          <p className="text-sm" style={{ color: "var(--ooe-ink-2)" }}>Loading…</p>
        ) : inquiries.length === 0 ? (
          <div className="py-20 text-center" style={{ border: "1px dashed var(--ooe-border)" }}>
            <div className="eyebrow mb-3">Empty</div>
            <p className="font-serif-display text-2xl" style={{ color: "var(--ooe-ink-2)" }}>No inquiries yet.</p>
          </div>
        ) : (
          <div className="space-y-5">
            {inquiries.map((i) => (
              <article key={i.id} className="p-6 lg:p-8" style={{ background: i.status === "new" ? "var(--ooe-bg-2)" : "var(--ooe-bg)", border: "1px solid var(--ooe-border)" }} data-testid={`inquiry-${i.id}`}>
                <div className="flex flex-wrap justify-between items-start gap-4">
                  <div>
                    <div className="font-serif-display text-2xl" style={{ color: "var(--ooe-ink)" }}>{i.name}</div>
                    <div className="text-[13px] mt-1" style={{ color: "var(--ooe-ink-2)" }}>
                      {i.email} {i.phone && `· ${i.phone}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 text-[11px]" style={{ color: "var(--ooe-ink-2)" }}>
                    <span className="eyebrow" style={{ color: i.status === "new" ? "var(--ooe-brand)" : "var(--ooe-ink-2)", fontSize: 10 }}>{i.status}</span>
                    <span>{new Date(i.created_at).toLocaleString()}</span>
                  </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-4 mt-5 text-[13px]">
                  <div><span className="label-line">Service</span>{i.service || "—"}</div>
                  <div><span className="label-line">Event date</span>{i.event_date || "—"}</div>
                  <div><span className="label-line">Guests</span>{i.guest_count || "—"}</div>
                </div>

                <p className="mt-5 text-[14px] leading-relaxed whitespace-pre-wrap" style={{ color: "var(--ooe-ink)" }}>{i.message}</p>

                <div className="mt-6 flex gap-3">
                  {i.status !== "read" && (
                    <button onClick={() => updateStatus(i.id, "read")} className="btn-ghost" data-testid={`mark-read-${i.id}`}>Mark read</button>
                  )}
                  {i.status !== "archived" && (
                    <button onClick={() => updateStatus(i.id, "archived")} className="btn-ghost" data-testid={`archive-${i.id}`}>Archive</button>
                  )}
                  {i.status === "archived" && (
                    <button onClick={() => updateStatus(i.id, "new")} className="btn-ghost" data-testid={`unarchive-${i.id}`}>Restore</button>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
